import React, { useState, useEffect } from 'react';
import { 
  Zap, TrendingUp, Target, Rocket, Mail, User,
  Phone, Briefcase, FileText, DollarSign, Users,
  Calendar, Globe, Check, ChevronRight, Search, Loader, ChevronDown, BarChart3
} from 'lucide-react';

const PlanningLandingPage = () => {
  const [activeTab, setActiveTab] = useState('plan');
  const [formData, setFormData] = useState({
    // Basic Info (auto-filled if available)
    name: '',
    email: '',
    phone: '',
    
    // Business Plan Questions
    businessName: '',
    businessDescription: '',
    companyValues: '',
    targetMarket: '',
    primaryRevenue: '',
    currentStage: '',
    launchTimeline: '',
    monthlyBudget: '',
    mainChallenge: '',
    existingWebsite: '',
    competitorUrls: '',
    additionalNotes: '',

    // Website Details (Optional - for client-summary.txt)
    businessType: '',
    platformType: '',
    expectedTraffic: '',
    expectedStorage: '',
    primaryColor: '',
    secondaryColor: '',
    backgroundColor: '',
    hasLogo: '',
    logoInHeader: '',
    fontStyle: '',
    brandTone: '',
    designTheme: '',
    layoutStyle: '',
    headerStyle: '',
    buttonStyle: '',
    animationLevel: '',
    imageStyle: '',
    revenueModel: '',
    pricingTiers: '',
    chatbotName: '',
    chatbotPersonality: '',
    emailConfirmations: '',
    emailNotifications: '',
    googleAnalytics: '',
    gaId: '',
    facebook: '',
    instagram: '',
    twitter: '',
    linkedin: '',
    vodVideoCount: '',
    vodDownloads: '',
    vodQuality: ''
  });
  
  // eslint-disable-next-line no-unused-vars
  const [submitted, setSubmitted] = useState(false); // Will be used for email submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Tab progression states
  const [planSubmitted, setPlanSubmitted] = useState(false);
  const [chartOfAccountsReviewed, setChartOfAccountsReviewed] = useState(false);
  
  // Chart of Accounts states
  const [accounts, setAccounts] = useState([]);
  const [accountCounters, setAccountCounters] = useState({
    assets: 1000,
    liabilities: 2000,
    equity: 3000,
    revenue: 4000,
    expenses: 5000
  });
  const [newAccount, setNewAccount] = useState({
    type: '',
    acctNumber: '',
    category: '',
    name: '',
    amount: '0'
  });
  const [accountsSorted, setAccountsSorted] = useState(false);
  const [skipChartOfAccounts, setSkipChartOfAccounts] = useState(false);
  const [processAnalysisComplete, setProcessAnalysisComplete] = useState(false);
  
  // Review submission states
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  
  // Domain search states
  const [domainSearch, setDomainSearch] = useState('');
  const [isCheckingDomain, setIsCheckingDomain] = useState(false);
  const [domainCheckResult, setDomainCheckResult] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [skipDomainSelection, setSkipDomainSelection] = useState(false);
  const [websiteDetailsExpanded, setWebsiteDetailsExpanded] = useState(false);

  // Auto-fill from localStorage on mount
  useEffect(() => {
    const getFromLocalStorage = (key) => {
      const itemStr = localStorage.getItem(key);
      if (!itemStr) return null;
      
      try {
        const item = JSON.parse(itemStr);
        const now = new Date().getTime();
        const hoursDiff = (now - item.timestamp) / (1000 * 60 * 60);
        
        if (hoursDiff > 24) {
          localStorage.removeItem(key);
          return null;
        }
        
        return item.value;
      } catch (e) {
        return itemStr;
      }
    };
    
    const savedName = getFromLocalStorage('userName');
    const savedEmail = getFromLocalStorage('userEmail');
    const savedPhone = getFromLocalStorage('userPhone');
    
    // Auto-fill main form (Plan Your Platform tab)
    setFormData(prev => ({
      ...prev,
      name: savedName || '',
      email: savedEmail || '',
      phone: savedPhone || ''
    }));
  }, []);

  // Load AdSense ads for all visible ad units
  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        // Find all uninitialized ad slots
        const adSlots = document.querySelectorAll('.adsbygoogle');
        
        adSlots.forEach((adSlot) => {
          // Check if this slot hasn't been initialized yet
          const status = adSlot.getAttribute('data-adsbygoogle-status');
          
          if (!status || status === '') {
            if (window.adsbygoogle) {
              (window.adsbygoogle = window.adsbygoogle || []).push({});
            }
          }
        });
      } catch (err) {
        console.error('AdSense error:', err);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [activeTab]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Save name, email, phone to localStorage with 24-hour expiration
    if (field === 'name' || field === 'email' || field === 'phone') {
      const item = {
        value: value,
        timestamp: new Date().getTime()
      };
      const storageKey = field === 'name' ? 'userName' : field === 'email' ? 'userEmail' : 'userPhone';
      localStorage.setItem(storageKey, JSON.stringify(item));
    }
  };

  const checkDomainAvailability = async () => {
    if (!domainSearch.trim()) return;
    
    setIsCheckingDomain(true);
    setDomainCheckResult(null);
    
    try {
      const response = await fetch('https://4n7w60zube.execute-api.us-east-1.amazonaws.com/check-domain', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: domainSearch.trim() })
      });
      
      const data = await response.json();
      setDomainCheckResult(data);
      
      if (data.available) {
        setSelectedDomain(domainSearch.trim());
      }
    } catch (error) {
      console.error('Domain check error:', error);
      setDomainCheckResult({ error: 'Failed to check domain availability. Please try again.' });
    } finally {
      setIsCheckingDomain(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Submit to Lambda endpoint
      const response = await fetch('https://4n7w60zube.execute-api.us-east-1.amazonaws.com/planning', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          timestamp: new Date().toISOString(),
          ...formData,
          selectedDomain: selectedDomain
        })
      });
      
      if (response.ok) {
        // Mark Plan tab as submitted and enable Chart of Accounts
        setPlanSubmitted(true);
        // Switch to Chart of Accounts tab
        setActiveTab('profit-hub');
      } else {
        alert('There was an error submitting your response. Please try again or contact us directly.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('There was an error submitting your response. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const questionnaireComplete = formData.businessName && formData.businessDescription;
  const canSubmit = formData.name && formData.email && formData.phone && questionnaireComplete && (selectedDomain || skipDomainSelection);

  // Chart of Accounts handlers
  const handleAccountTypeChange = (type) => {
    let acctNumber = '';
    switch(type) {
      case 'Assets':
        acctNumber = accountCounters.assets;
        break;
      case 'Liabilities':
        acctNumber = accountCounters.liabilities;
        break;
      case 'Owner\'s Equity':
        acctNumber = accountCounters.equity;
        break;
      case 'Expenses':
        acctNumber = accountCounters.expenses;
        break;
      case 'Revenue':
        acctNumber = accountCounters.revenue;
        break;
      default:
        acctNumber = '';
    }
    setNewAccount({ ...newAccount, type, acctNumber });
  };

  const handleAddAccount = (e) => {
    e.preventDefault();
    
    if (!newAccount.type || !newAccount.name) {
      alert('Please fill in the Type and Name/Description fields.');
      return;
    }

    // Add the account to the list
    setAccounts([...accounts, { ...newAccount }]);

    // Increment the counter for this account type
    const counterKey = newAccount.type === 'Assets' ? 'assets' 
      : newAccount.type === 'Liabilities' ? 'liabilities'
      : newAccount.type === 'Owner\'s Equity' ? 'equity'
      : newAccount.type === 'Expenses' ? 'expenses'
      : 'revenue';

    setAccountCounters({
      ...accountCounters,
      [counterKey]: accountCounters[counterKey] + 1
    });

    // Reset the form
    setNewAccount({
      type: '',
      acctNumber: '',
      category: '',
      name: '',
      amount: '0'
    });
  };

  const handleReviewAccounts = () => {
    // Sort accounts by account number
    const sorted = [...accounts].sort((a, b) => a.acctNumber - b.acctNumber);
    setAccounts(sorted);
    setAccountsSorted(true);
    setChartOfAccountsReviewed(true);
    // Keep user on current tab to review sorted accounts
    // User can manually navigate to value-gap tab when ready
  };

  const handleReviewSubmit = async () => {
    setReviewSubmitting(true);
    
    try {
      const submissionData = {
        planData: formData,
        chartOfAccounts: {
          skipped: skipChartOfAccounts,
          accounts: accounts
        },
        processAnalysis: {
          completed: processAnalysisComplete
        },
        submittedAt: new Date().toISOString()
      };

      console.log('Submitting review to API...');
      const response = await fetch('https://4n7w60zube.execute-api.us-east-1.amazonaws.com/review-submission', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });

      console.log('Response status:', response.status);

      // If we got a 200 response, the submission was successful (even if parsing fails)
      if (response.status === 200) {
        try {
          const result = await response.json();
          console.log('Response parsed successfully:', result);
          if (result.success) {
            console.log('Review submitted successfully with confirmation');
            setReviewSubmitted(true);
            setSubmitted(true);
          } else {
            // 200 status but success=false - log warning but don't fail
            console.warn('200 response but success=false:', result);
            setReviewSubmitted(true);
            setSubmitted(true);
          }
        } catch (parseError) {
          // JSON parsing failed, but we got 200 status so submission succeeded
          console.warn('Response parsing failed but status was 200, assuming success:', parseError);
          setReviewSubmitted(true);
          setSubmitted(true);
        }
      } else {
        // Non-200 status code
        let errorMessage = 'Submission failed';
        try {
          const result = await response.json();
          errorMessage = result.message || result.error || errorMessage;
        } catch (e) {
          // Couldn't parse error response
          errorMessage = `Server returned status ${response.status}`;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      // Only network errors or non-200 responses get here
      console.error('Review submission error:', error);
      // Only show alert for actual failures (not for parsing issues on successful 200 responses)
      if (error.message && !error.message.includes('JSON')) {
        alert('There was an error submitting your review. Please try again or contact us directly at larrycoons@larrycoons.com');
      }
    } finally {
      setReviewSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b-2 border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <a href="/step0/" className="flex items-center hover:opacity-80 transition">
                <img 
                  src="/step0/Sovereign%20Stack%20Logo.png" 
                  alt="Sovereign Logic" 
                  className="w-12 h-12 rounded-lg mr-4 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-12 h-12 rounded-lg mr-4 bg-gradient-to-br from-blue-600 to-blue-700 items-center justify-center shadow-md" style={{ display: 'none' }}>
                  <span className="text-white text-2xl font-bold" style={{ fontFamily: 'Georgia, serif' }}>S</span>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'system-ui, -apple-system, sans-serif', letterSpacing: '-0.02em' }}>
                    Platform Planning
                  </h1>
                  <p className="text-sm text-gray-600 font-medium" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                    by Sovereign Logic
                  </p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>

      {submitted ? (
        // Success Message
        <div className="pt-12 pb-12 px-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h2>
              <p className="text-xl text-gray-600 mb-8">
                Your platform planning response has been received. We'll review your information and reach out within 24 hours to discuss next steps.
              </p>
              <div className="space-y-4">
                <a 
                  href="https://www.larrycoons.com/step0"
                  className="inline-block bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition"
                >
                  Explore More Options
                </a>
                <div>
                  <a 
                    href="/step0/"
                    className="text-gray-600 hover:text-gray-800"
                  >
                    ← Back to Home
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Tabs */}
          <div className="bg-white border-b border-gray-200">
            <div className="max-w-6xl mx-auto px-4">
              <div className="flex space-x-1 overflow-x-auto">
                <button
                  onClick={() => setActiveTab('plan')}
                  className={`px-6 py-4 font-semibold text-sm whitespace-nowrap border-b-2 transition ${
                    activeTab === 'plan'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }`}
                >
                  <Rocket className="inline w-4 h-4 mr-2" />
                  Plan Your Platform
                </button>
                <button
                  onClick={() => setActiveTab('profit-hub')}
                  disabled={!planSubmitted}
                  className={`px-6 py-4 font-semibold text-sm whitespace-nowrap border-b-2 transition ${
                    activeTab === 'profit-hub'
                      ? 'border-blue-600 text-blue-600'
                      : !planSubmitted
                        ? 'border-transparent text-gray-400 cursor-not-allowed'
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }`}
                >
                  <TrendingUp className="inline w-4 h-4 mr-2" />
                  Chart of Accounts
                  {!planSubmitted && <span className="ml-2 text-xs">(🔒 Complete Plan first)</span>}
                </button>
                {/* HIDDEN: Process Analysis tab - uncomment to restore */}
                {/* <button
                  onClick={() => setActiveTab('value-gap')}
                  disabled={!chartOfAccountsReviewed}
                  className={`px-6 py-4 font-semibold text-sm whitespace-nowrap border-b-2 transition ${
                    activeTab === 'value-gap'
                      ? 'border-blue-600 text-blue-600'
                      : !chartOfAccountsReviewed
                        ? 'border-transparent text-gray-400 cursor-not-allowed'
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }`}
                >
                  <Target className="inline w-4 h-4 mr-2" />
                  Process Analysis
                  {!chartOfAccountsReviewed && <span className="ml-2 text-xs">(🔒 Complete Chart first)</span>}
                </button> */}
                <button
                  onClick={() => setActiveTab('auto-stack')}
                  disabled={!chartOfAccountsReviewed}
                  className={`px-6 py-4 font-semibold text-sm whitespace-nowrap border-b-2 transition ${
                    activeTab === 'auto-stack'
                      ? 'border-blue-600 text-blue-600'
                      : !chartOfAccountsReviewed
                        ? 'border-transparent text-gray-400 cursor-not-allowed'
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }`}
                >
                  <Zap className="inline w-4 h-4 mr-2" />
                  Review
                  {!chartOfAccountsReviewed && <span className="ml-2 text-xs">(🔒 Complete Chart first)</span>}
                </button>
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <div className="pt-12 pb-12 px-4">
            <div className="max-w-6xl mx-auto">
              
              {/* Review Tab */}
              {activeTab === 'auto-stack' && (
                <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 animate-fadeIn">
                  {/* Header */}
                  <div className="text-center mb-8">
                    <a href="/step0/" className="inline-block mb-4">
                      <img 
                        src="/step0/Sovereign%20Stack%20Logo.png" 
                        alt="Sovereign Stack" 
                        className="w-16 h-16 rounded-lg mx-auto shadow-md hover:shadow-lg transition-shadow"
                      />
                    </a>
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                      Review Your Planning Summary
                    </h2>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                      Review all your entries before submitting
                    </p>
                  </div>

                  <div className="max-w-4xl mx-auto">
                    {/* Platform Plan Summary */}
                    <div className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
                      <div className="flex items-start mb-4">
                        <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                          <Rocket className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-4">Platform Plan</h3>
                          
                          {/* Contact Information */}
                          <div className="mb-6">
                            <h4 className="text-lg font-semibold text-gray-800 mb-3 border-b pb-2">Contact Information</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-gray-600 font-semibold">Name:</p>
                                <p className="text-gray-900">{formData.name || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Email:</p>
                                <p className="text-gray-900">{formData.email || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Phone:</p>
                                <p className="text-gray-900">{formData.phone || 'Not provided'}</p>
                              </div>
                            </div>
                          </div>

                          {/* Business Details */}
                          <div className="mb-6">
                            <h4 className="text-lg font-semibold text-gray-800 mb-3 border-b pb-2">Business Details</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-gray-600 font-semibold">Business Name:</p>
                                <p className="text-gray-900">{formData.businessName || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Business Type:</p>
                                <p className="text-gray-900">{formData.businessType || 'Not provided'}</p>
                              </div>
                              <div className="md:col-span-2">
                                <p className="text-gray-600 font-semibold">Business Description:</p>
                                <p className="text-gray-900">{formData.businessDescription || 'Not provided'}</p>
                              </div>
                              <div className="md:col-span-2">
                                <p className="text-gray-600 font-semibold">Company Values:</p>
                                <p className="text-gray-900">{formData.companyValues || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Target Market:</p>
                                <p className="text-gray-900">{formData.targetMarket || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Primary Revenue:</p>
                                <p className="text-gray-900">{formData.primaryRevenue || 'Not provided'}</p>
                              </div>
                            </div>
                          </div>

                          {/* Platform Details */}
                          <div className="mb-6">
                            <h4 className="text-lg font-semibold text-gray-800 mb-3 border-b pb-2">Platform Details</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-gray-600 font-semibold">Platform Name:</p>
                                <p className="text-gray-900">{formData.platformName || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Platform Type:</p>
                                <p className="text-gray-900">{formData.platformType || 'Not provided'}</p>
                              </div>
                              <div className="md:col-span-2">
                                <p className="text-gray-600 font-semibold">Platform Description:</p>
                                <p className="text-gray-900">{formData.platformDescription || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Revenue Model:</p>
                                <p className="text-gray-900">{formData.revenueModel || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Pricing Tiers:</p>
                                <p className="text-gray-900">{formData.pricingTiers || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Domain Name:</p>
                                <p className="text-gray-900">
                                  {formData.skipDomainSelection ? 'Skipped - will decide later' : (formData.domainName || 'Not provided')}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Expected Traffic:</p>
                                <p className="text-gray-900">{formData.expectedTraffic || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Expected Storage:</p>
                                <p className="text-gray-900">{formData.expectedStorage || 'Not provided'}</p>
                              </div>
                            </div>
                          </div>

                          {/* Platform Features */}
                          <div className="mb-6">
                            <h4 className="text-lg font-semibold text-gray-800 mb-3 border-b pb-2">Platform Features</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-gray-600 font-semibold">User Management:</p>
                                <p className="text-gray-900">{formData.userManagement || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Content Types:</p>
                                <p className="text-gray-900">{formData.contentTypes || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Payment Integration:</p>
                                <p className="text-gray-900">{formData.paymentIntegration || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Third-Party Integrations:</p>
                                <p className="text-gray-900">{formData.thirdPartyIntegrations || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Chatbot Name:</p>
                                <p className="text-gray-900">{formData.chatbotName || 'Not provided'}</p>
                              </div>
                            </div>
                          </div>

                          {/* Live Streaming */}
                          {formData.liveStreaming && (
                            <div className="mb-6">
                              <h4 className="text-lg font-semibold text-gray-800 mb-3 border-b pb-2">Live Streaming</h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                  <p className="text-gray-600 font-semibold">Live Streaming:</p>
                                  <p className="text-gray-900">Enabled</p>
                                </div>
                                <div>
                                  <p className="text-gray-600 font-semibold">Expected Live Viewers:</p>
                                  <p className="text-gray-900">{formData.liveViewers || 'Not provided'}</p>
                                </div>
                                <div>
                                  <p className="text-gray-600 font-semibold">Concurrent Viewers:</p>
                                  <p className="text-gray-900">{formData.liveConcurrent || 'Not provided'}</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* VOD */}
                          {formData.vodEnabled && (
                            <div className="mb-6">
                              <h4 className="text-lg font-semibold text-gray-800 mb-3 border-b pb-2">Video on Demand (VOD)</h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                  <p className="text-gray-600 font-semibold">VOD Enabled:</p>
                                  <p className="text-gray-900">Yes</p>
                                </div>
                                <div>
                                  <p className="text-gray-600 font-semibold">Video Count:</p>
                                  <p className="text-gray-900">{formData.vodVideoCount || 'Not provided'}</p>
                                </div>
                                <div>
                                  <p className="text-gray-600 font-semibold">Downloads Allowed:</p>
                                  <p className="text-gray-900">{formData.vodDownloads ? 'Yes' : 'No'}</p>
                                </div>
                                <div>
                                  <p className="text-gray-600 font-semibold">Quality:</p>
                                  <p className="text-gray-900">{formData.vodQuality || 'Not provided'}</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Design Preferences */}
                          <div className="mb-6">
                            <h4 className="text-lg font-semibold text-gray-800 mb-3 border-b pb-2">Design Preferences</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-gray-600 font-semibold">Primary Color:</p>
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded border" style={{backgroundColor: formData.primaryColor || '#3b82f6'}}></div>
                                  <p className="text-gray-900">{formData.primaryColor || 'Not provided'}</p>
                                </div>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Secondary Color:</p>
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded border" style={{backgroundColor: formData.secondaryColor || '#8b5cf6'}}></div>
                                  <p className="text-gray-900">{formData.secondaryColor || 'Not provided'}</p>
                                </div>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Background Color:</p>
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded border" style={{backgroundColor: formData.backgroundColor || '#ffffff'}}></div>
                                  <p className="text-gray-900">{formData.backgroundColor || 'Not provided'}</p>
                                </div>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Font Style:</p>
                                <p className="text-gray-900">{formData.fontStyle || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Brand Tone:</p>
                                <p className="text-gray-900">{formData.brandTone || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Design Theme:</p>
                                <p className="text-gray-900">{formData.designTheme || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Layout Style:</p>
                                <p className="text-gray-900">{formData.layoutStyle || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Header Style:</p>
                                <p className="text-gray-900">{formData.headerStyle || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Button Style:</p>
                                <p className="text-gray-900">{formData.buttonStyle || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Animation Level:</p>
                                <p className="text-gray-900">{formData.animationLevel || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Image Style:</p>
                                <p className="text-gray-900">{formData.imageStyle || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Logo:</p>
                                <p className="text-gray-900">{formData.hasLogo ? (formData.logoInHeader ? 'Yes, in header' : 'Yes') : 'No logo'}</p>
                              </div>
                            </div>
                          </div>

                          {/* Project Planning */}
                          <div className="mb-6">
                            <h4 className="text-lg font-semibold text-gray-800 mb-3 border-b pb-2">Project Planning</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-gray-600 font-semibold">Current Stage:</p>
                                <p className="text-gray-900">{formData.currentStage || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Launch Timeline:</p>
                                <p className="text-gray-900">{formData.launchTimeline || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Monthly Budget:</p>
                                <p className="text-gray-900">{formData.monthlyBudget || 'Not provided'}</p>
                              </div>
                              <div className="md:col-span-2">
                                <p className="text-gray-600 font-semibold">Main Challenge:</p>
                                <p className="text-gray-900">{formData.mainChallenge || 'Not provided'}</p>
                              </div>
                            </div>
                          </div>

                          {/* Additional Information */}
                          <div className="mb-6">
                            <h4 className="text-lg font-semibold text-gray-800 mb-3 border-b pb-2">Additional Information</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-gray-600 font-semibold">Existing Website:</p>
                                <p className="text-gray-900">{formData.existingWebsite || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-semibold">Competitor URLs:</p>
                                <p className="text-gray-900">{formData.competitorUrls || 'Not provided'}</p>
                              </div>
                              <div className="md:col-span-2">
                                <p className="text-gray-600 font-semibold">Additional Notes:</p>
                                <p className="text-gray-900">{formData.additionalNotes || 'Not provided'}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Chart of Accounts Summary */}
                    <div className="bg-gradient-to-br from-green-50 to-teal-50 border-2 border-green-200 rounded-xl p-6 mb-6">
                      <div className="flex items-start mb-4">
                        <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                          <TrendingUp className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-4">Chart of Accounts</h3>
                          {skipChartOfAccounts ? (
                            <p className="text-gray-700">Skipped - Will set up later</p>
                          ) : accounts.length > 0 ? (
                            <>
                              <p className="text-gray-700 mb-3">
                                <span className="font-semibold">{accounts.length}</span> account{accounts.length !== 1 ? 's' : ''} configured
                              </p>
                              <div className="bg-white rounded-lg p-4 max-h-64 overflow-y-auto">
                                <table className="w-full text-sm">
                                  <thead className="border-b sticky top-0 bg-white">
                                    <tr>
                                      <th className="text-left py-2 px-2">Number</th>
                                      <th className="text-left py-2 px-2">Name</th>
                                      <th className="text-left py-2 px-2">Type</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {accounts.map((account, idx) => (
                                      <tr key={idx} className="border-b last:border-0">
                                        <td className="py-2 px-2">{account.number}</td>
                                        <td className="py-2 px-2">{account.name}</td>
                                        <td className="py-2 px-2 capitalize">{account.type}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </>
                          ) : (
                            <p className="text-gray-700">No accounts added yet</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Process Analysis Summary */}
                    <div className="bg-gradient-to-br from-orange-50 to-yellow-50 border-2 border-orange-200 rounded-xl p-6 mb-6">
                      <div className="flex items-start mb-4">
                        <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                          <BarChart3 className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-4">Process Analysis (DMAIC)</h3>
                          <p className="text-gray-700">
                            Status: <span className="font-semibold">
                              {processAnalysisComplete ? 'Completed ✓' : 'Not completed'}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-8 text-center mb-6">
                      <h4 className="text-2xl font-bold text-white mb-3">
                        Ready to Submit?
                      </h4>
                      <p className="text-blue-100 mb-6">
                        We'll send a copy of your planning summary to your email and our team for review.
                      </p>
                      <button
                        onClick={handleReviewSubmit}
                        disabled={reviewSubmitting || reviewSubmitted}
                        className={`px-8 py-4 rounded-lg font-bold text-lg transition shadow-lg hover:shadow-xl inline-flex items-center ${
                          reviewSubmitting || reviewSubmitted
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-white text-blue-600 hover:bg-blue-50'
                        }`}
                      >
                        {reviewSubmitting ? (
                          <>
                            <Loader className="w-5 h-5 mr-2 animate-spin" />
                            Submitting...
                          </>
                        ) : reviewSubmitted ? (
                          <>
                            <Check className="w-5 h-5 mr-2" />
                            Submitted!
                          </>
                        ) : (
                          <>
                            <Mail className="w-5 h-5 mr-2" />
                            Submit Planning Summary
                          </>
                        )}
                      </button>
                    </div>

                    {/* Ad Banner */}
                    <div className="my-8 p-5 bg-white rounded-2xl shadow-sm">
                      <p className="text-center text-xs text-gray-500 mb-3">Advertisement</p>
                      <ins className="adsbygoogle"
                        style={{ display: 'block' }}
                        data-ad-client="ca-pub-6663686986943720"
                        data-ad-format="auto"
                        data-full-width-responsive="true"></ins>
                    </div>
                  </div>
                </div>
              )}

              {/* Chart of Accounts Tab */}
              {activeTab === 'profit-hub' && (
                <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 animate-fadeIn">
                  {/* Header */}
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <TrendingUp className="w-8 h-8 text-green-600" />
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                      Chart of Accounts
                    </h2>
                    <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-6">
                      Build your financial foundation by organizing all your business accounts. A proper chart of accounts helps you track income, expenses, assets, and liabilities—giving you complete visibility into your business finances.
                    </p>
                    <div className="bg-blue-50 border-l-4 border-blue-600 p-4 max-w-2xl mx-auto text-left">
                      <p className="text-sm text-gray-700">
                        <strong>Why this matters:</strong> Your chart of accounts is the backbone of your accounting system. It categorizes every financial transaction, making it easy to generate financial statements, track profitability, and make data-driven decisions.
                      </p>
                    </div>
                  </div>

                  {/* Account Sections */}
                  <div className="max-w-5xl mx-auto space-y-8">
                    
                    {/* Assets Section */}
                    <div className="border-2 border-gray-200 rounded-xl p-6">
                      <div className="mb-4">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">💰 Assets (1000-1999)</h3>
                        <p className="text-sm text-gray-600">
                          What your business owns that has value (cash, equipment, inventory, accounts receivable)
                        </p>
                      </div>
                      
                      <form onSubmit={(e) => { e.preventDefault(); handleAddAccount(e); }} className="bg-gray-50 p-4 rounded-lg mb-4">
                        <div className="grid md:grid-cols-5 gap-4">
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Type</label>
                            <select
                              value={newAccount.type === 'Assets' ? newAccount.type : ''}
                              onChange={(e) => handleAccountTypeChange(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            >
                              <option value="">Select...</option>
                              <option value="Assets">Assets</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Acct #</label>
                            <input
                              type="number"
                              value={newAccount.type === 'Assets' ? newAccount.acctNumber : ''}
                              readOnly
                              className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-100 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Category (Optional)</label>
                            <input
                              type="text"
                              value={newAccount.type === 'Assets' ? newAccount.category : ''}
                              onChange={(e) => setNewAccount({ ...newAccount, category: e.target.value })}
                              placeholder="e.g., Current Assets"
                              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Name/Description *</label>
                            <input
                              type="text"
                              value={newAccount.type === 'Assets' ? newAccount.name : ''}
                              onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
                              placeholder="e.g., Cash in Bank"
                              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Amount</label>
                            <input
                              type="number"
                              value={newAccount.type === 'Assets' ? newAccount.amount : '0'}
                              onChange={(e) => setNewAccount({ ...newAccount, amount: e.target.value })}
                              placeholder="$0"
                              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            />
                          </div>
                        </div>
                        <button
                          type="submit"
                          disabled={newAccount.type !== 'Assets' || !newAccount.name}
                          className="mt-3 w-full bg-green-600 text-white px-4 py-2 rounded font-semibold hover:bg-green-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                        >
                          + Add Asset Account
                        </button>
                      </form>

                      {/* Display Assets */}
                      <div className="space-y-2">
                        {accounts.filter(acc => acc.type === 'Assets').map((acc, idx) => (
                          <div key={idx} className="bg-white border border-gray-200 rounded p-3 flex justify-between items-center text-sm">
                            <div className="flex-1">
                              <span className="font-bold text-gray-700">{acc.acctNumber}</span>
                              {acc.category && <span className="mx-2 text-gray-500">|</span>}
                              {acc.category && <span className="text-gray-600">{acc.category}</span>}
                              <span className="mx-2">-</span>
                              <span className="font-semibold">{acc.name}</span>
                            </div>
                            <span className="text-green-600 font-bold">${acc.amount}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Liabilities Section */}
                    <div className="border-2 border-gray-200 rounded-xl p-6">
                      <div className="mb-4">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">📋 Liabilities (2000-2999)</h3>
                        <p className="text-sm text-gray-600">
                          What your business owes (loans, accounts payable, credit card debt)
                        </p>
                      </div>
                      
                      <form onSubmit={(e) => { e.preventDefault(); handleAddAccount(e); }} className="bg-gray-50 p-4 rounded-lg mb-4">
                        <div className="grid md:grid-cols-5 gap-4">
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Type</label>
                            <select
                              value={newAccount.type === 'Liabilities' ? newAccount.type : ''}
                              onChange={(e) => handleAccountTypeChange(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            >
                              <option value="">Select...</option>
                              <option value="Liabilities">Liabilities</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Acct #</label>
                            <input
                              type="number"
                              value={newAccount.type === 'Liabilities' ? newAccount.acctNumber : ''}
                              readOnly
                              className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-100 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Category (Optional)</label>
                            <input
                              type="text"
                              value={newAccount.type === 'Liabilities' ? newAccount.category : ''}
                              onChange={(e) => setNewAccount({ ...newAccount, category: e.target.value })}
                              placeholder="e.g., Current Liabilities"
                              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Name/Description *</label>
                            <input
                              type="text"
                              value={newAccount.type === 'Liabilities' ? newAccount.name : ''}
                              onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
                              placeholder="e.g., Accounts Payable"
                              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Amount</label>
                            <input
                              type="number"
                              value={newAccount.type === 'Liabilities' ? newAccount.amount : '0'}
                              onChange={(e) => setNewAccount({ ...newAccount, amount: e.target.value })}
                              placeholder="$0"
                              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            />
                          </div>
                        </div>
                        <button
                          type="submit"
                          disabled={newAccount.type !== 'Liabilities' || !newAccount.name}
                          className="mt-3 w-full bg-red-600 text-white px-4 py-2 rounded font-semibold hover:bg-red-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                        >
                          + Add Liability Account
                        </button>
                      </form>

                      {/* Display Liabilities */}
                      <div className="space-y-2">
                        {accounts.filter(acc => acc.type === 'Liabilities').map((acc, idx) => (
                          <div key={idx} className="bg-white border border-gray-200 rounded p-3 flex justify-between items-center text-sm">
                            <div className="flex-1">
                              <span className="font-bold text-gray-700">{acc.acctNumber}</span>
                              {acc.category && <span className="mx-2 text-gray-500">|</span>}
                              {acc.category && <span className="text-gray-600">{acc.category}</span>}
                              <span className="mx-2">-</span>
                              <span className="font-semibold">{acc.name}</span>
                            </div>
                            <span className="text-red-600 font-bold">${acc.amount}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Owner's Equity Section */}
                    <div className="border-2 border-gray-200 rounded-xl p-6">
                      <div className="mb-4">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">👤 Owner's Equity (3000-3999)</h3>
                        <p className="text-sm text-gray-600">
                          Owner investments and retained earnings (capital contributions, draws, profits)
                        </p>
                      </div>
                      
                      <form onSubmit={(e) => { e.preventDefault(); handleAddAccount(e); }} className="bg-gray-50 p-4 rounded-lg mb-4">
                        <div className="grid md:grid-cols-5 gap-4">
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Type</label>
                            <select
                              value={newAccount.type === 'Owner\'s Equity' ? newAccount.type : ''}
                              onChange={(e) => handleAccountTypeChange(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            >
                              <option value="">Select...</option>
                              <option value="Owner's Equity">Owner's Equity</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Acct #</label>
                            <input
                              type="number"
                              value={newAccount.type === 'Owner\'s Equity' ? newAccount.acctNumber : ''}
                              readOnly
                              className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-100 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Category (Optional)</label>
                            <input
                              type="text"
                              value={newAccount.type === 'Owner\'s Equity' ? newAccount.category : ''}
                              onChange={(e) => setNewAccount({ ...newAccount, category: e.target.value })}
                              placeholder="e.g., Capital"
                              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Name/Description *</label>
                            <input
                              type="text"
                              value={newAccount.type === 'Owner\'s Equity' ? newAccount.name : ''}
                              onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
                              placeholder="e.g., Owner Capital"
                              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Amount</label>
                            <input
                              type="number"
                              value={newAccount.type === 'Owner\'s Equity' ? newAccount.amount : '0'}
                              onChange={(e) => setNewAccount({ ...newAccount, amount: e.target.value })}
                              placeholder="$0"
                              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            />
                          </div>
                        </div>
                        <button
                          type="submit"
                          disabled={newAccount.type !== 'Owner\'s Equity' || !newAccount.name}
                          className="mt-3 w-full bg-blue-600 text-white px-4 py-2 rounded font-semibold hover:bg-blue-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                        >
                          + Add Equity Account
                        </button>
                      </form>

                      {/* Display Equity */}
                      <div className="space-y-2">
                        {accounts.filter(acc => acc.type === 'Owner\'s Equity').map((acc, idx) => (
                          <div key={idx} className="bg-white border border-gray-200 rounded p-3 flex justify-between items-center text-sm">
                            <div className="flex-1">
                              <span className="font-bold text-gray-700">{acc.acctNumber}</span>
                              {acc.category && <span className="mx-2 text-gray-500">|</span>}
                              {acc.category && <span className="text-gray-600">{acc.category}</span>}
                              <span className="mx-2">-</span>
                              <span className="font-semibold">{acc.name}</span>
                            </div>
                            <span className="text-blue-600 font-bold">${acc.amount}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Expenses Section */}
                    <div className="border-2 border-gray-200 rounded-xl p-6">
                      <div className="mb-4">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">💸 Expenses (5000-5999)</h3>
                        <p className="text-sm text-gray-600">
                          Costs to run your business (rent, salaries, utilities, marketing)
                        </p>
                      </div>
                      
                      <form onSubmit={(e) => { e.preventDefault(); handleAddAccount(e); }} className="bg-gray-50 p-4 rounded-lg mb-4">
                        <div className="grid md:grid-cols-5 gap-4">
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Type</label>
                            <select
                              value={newAccount.type === 'Expenses' ? newAccount.type : ''}
                              onChange={(e) => handleAccountTypeChange(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            >
                              <option value="">Select...</option>
                              <option value="Expenses">Expenses</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Acct #</label>
                            <input
                              type="number"
                              value={newAccount.type === 'Expenses' ? newAccount.acctNumber : ''}
                              readOnly
                              className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-100 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Category (Optional)</label>
                            <input
                              type="text"
                              value={newAccount.type === 'Expenses' ? newAccount.category : ''}
                              onChange={(e) => setNewAccount({ ...newAccount, category: e.target.value })}
                              placeholder="e.g., Operating Expenses"
                              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Name/Description *</label>
                            <input
                              type="text"
                              value={newAccount.type === 'Expenses' ? newAccount.name : ''}
                              onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
                              placeholder="e.g., Office Rent"
                              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Amount</label>
                            <input
                              type="number"
                              value={newAccount.type === 'Expenses' ? newAccount.amount : '0'}
                              onChange={(e) => setNewAccount({ ...newAccount, amount: e.target.value })}
                              placeholder="$0"
                              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            />
                          </div>
                        </div>
                        <button
                          type="submit"
                          disabled={newAccount.type !== 'Expenses' || !newAccount.name}
                          className="mt-3 w-full bg-orange-600 text-white px-4 py-2 rounded font-semibold hover:bg-orange-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                        >
                          + Add Expense Account
                        </button>
                      </form>

                      {/* Display Expenses */}
                      <div className="space-y-2">
                        {accounts.filter(acc => acc.type === 'Expenses').map((acc, idx) => (
                          <div key={idx} className="bg-white border border-gray-200 rounded p-3 flex justify-between items-center text-sm">
                            <div className="flex-1">
                              <span className="font-bold text-gray-700">{acc.acctNumber}</span>
                              {acc.category && <span className="mx-2 text-gray-500">|</span>}
                              {acc.category && <span className="text-gray-600">{acc.category}</span>}
                              <span className="mx-2">-</span>
                              <span className="font-semibold">{acc.name}</span>
                            </div>
                            <span className="text-orange-600 font-bold">${acc.amount}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Revenue Section */}
                    <div className="border-2 border-gray-200 rounded-xl p-6">
                      <div className="mb-4">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">💵 Revenue/Income (4000-4999)</h3>
                        <p className="text-sm text-gray-600">
                          Income from sales and services (product sales, service fees, consulting revenue)
                        </p>
                      </div>
                      
                      <form onSubmit={(e) => { e.preventDefault(); handleAddAccount(e); }} className="bg-gray-50 p-4 rounded-lg mb-4">
                        <div className="grid md:grid-cols-5 gap-4">
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Type</label>
                            <select
                              value={newAccount.type === 'Revenue' ? newAccount.type : ''}
                              onChange={(e) => handleAccountTypeChange(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            >
                              <option value="">Select...</option>
                              <option value="Revenue">Revenue</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Acct #</label>
                            <input
                              type="number"
                              value={newAccount.type === 'Revenue' ? newAccount.acctNumber : ''}
                              readOnly
                              className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-100 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Category (Optional)</label>
                            <input
                              type="text"
                              value={newAccount.type === 'Revenue' ? newAccount.category : ''}
                              onChange={(e) => setNewAccount({ ...newAccount, category: e.target.value })}
                              placeholder="e.g., Sales Revenue"
                              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Name/Description *</label>
                            <input
                              type="text"
                              value={newAccount.type === 'Revenue' ? newAccount.name : ''}
                              onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
                              placeholder="e.g., Product Sales"
                              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Amount</label>
                            <input
                              type="number"
                              value={newAccount.type === 'Revenue' ? newAccount.amount : '0'}
                              onChange={(e) => setNewAccount({ ...newAccount, amount: e.target.value })}
                              placeholder="$0"
                              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            />
                          </div>
                        </div>
                        <button
                          type="submit"
                          disabled={newAccount.type !== 'Revenue' || !newAccount.name}
                          className="mt-3 w-full bg-purple-600 text-white px-4 py-2 rounded font-semibold hover:bg-purple-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                        >
                          + Add Revenue Account
                        </button>
                      </form>

                      {/* Display Revenue */}
                      <div className="space-y-2">
                        {accounts.filter(acc => acc.type === 'Revenue').map((acc, idx) => (
                          <div key={idx} className="bg-white border border-gray-200 rounded p-3 flex justify-between items-center text-sm">
                            <div className="flex-1">
                              <span className="font-bold text-gray-700">{acc.acctNumber}</span>
                              {acc.category && <span className="mx-2 text-gray-500">|</span>}
                              {acc.category && <span className="text-gray-600">{acc.category}</span>}
                              <span className="mx-2">-</span>
                              <span className="font-semibold">{acc.name}</span>
                            </div>
                            <span className="text-purple-600 font-bold">${acc.amount}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Skip Chart of Accounts Option */}
                    {accounts.length === 0 && (
                      <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={skipChartOfAccounts}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setSkipChartOfAccounts(isChecked);
                              if (isChecked) {
                                setChartOfAccountsReviewed(true);
                              } else {
                                setChartOfAccountsReviewed(false);
                              }
                            }}
                            className="w-5 h-5 text-blue-600 rounded"
                          />
                          <span className="text-gray-700 font-medium">
                            I'll discuss the Chart of Accounts during the consultation
                          </span>
                        </label>
                        {skipChartOfAccounts && (
                          <p className="mt-2 text-sm text-green-600 font-medium">✓ You can now proceed to the Review tab</p>
                        )}
                      </div>
                    )}

                    {/* Review Button */}
                    {accounts.length > 0 && (
                      <div className="bg-gradient-to-r from-blue-50 to-green-50 border-2 border-blue-300 rounded-xl p-8 text-center">
                        <h3 className="text-xl font-bold text-gray-900 mb-3">Complete Your Chart of Accounts</h3>
                        <p className="text-gray-700 mb-6">
                          You've added {accounts.length} account{accounts.length !== 1 ? 's' : ''}. Click below to complete this section and unlock the Review tab.
                        </p>
                        <button
                          onClick={handleReviewAccounts}
                          className="bg-gradient-to-r from-blue-600 to-green-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:from-blue-700 hover:to-green-700 transition shadow-lg hover:shadow-xl"
                        >
                          {accountsSorted ? '✓ Chart Complete - Go to Review →' : '✓ Complete Chart of Accounts'}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Ad Banner */}
                  <div className="my-8 p-5 bg-white rounded-2xl shadow-sm">
                    <p className="text-center text-xs text-gray-500 mb-3">Advertisement</p>
                    <ins className="adsbygoogle"
                      style={{ display: 'block' }}
                      data-ad-client="ca-pub-6663686986943720"
                      data-ad-format="auto"
                      data-full-width-responsive="true"></ins>
                  </div>
                </div>
              )}


              {/* HIDDEN: Process Analysis (Value Gap) Tab - change 'false &&' to 'true &&' to restore */}
              {false && activeTab === 'value-gap' && (
                <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 animate-fadeIn">
                  {/* Header Section */}
                  <div className="text-center mb-8">
                    <a href="/step0/" className="inline-block mb-4">
                      <img 
                        src="/step0/Sovereign%20Stack%20Logo.png" 
                        alt="Sovereign Stack" 
                        className="w-16 h-16 rounded-lg mx-auto shadow-md hover:shadow-lg transition-shadow"
                      />
                    </a>
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                      Process Analysis: DMAIC Framework
                    </h2>
                    <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-6">
                      Using Six Sigma principles to optimize your business processes
                    </p>
                  </div>

                  {/* DMAIC Introduction */}
                  <div className="max-w-4xl mx-auto mb-12">
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-l-4 border-blue-600 p-8 rounded-r-lg mb-8">
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">What is DMAIC?</h3>
                      <p className="text-gray-700 mb-4">
                        DMAIC is a data-driven quality strategy used to improve processes. It's an integral part of Six Sigma methodology and stands for:
                      </p>
                      <div className="space-y-3 text-gray-700">
                        <p><strong className="text-blue-600">Define:</strong> Clearly articulate the problem, goals, and customer requirements</p>
                        <p><strong className="text-blue-600">Measure:</strong> Collect data to establish baseline metrics and understand current performance</p>
                        <p><strong className="text-blue-600">Analyze:</strong> Examine data to identify root causes of defects or inefficiencies</p>
                        <p><strong className="text-blue-600">Improve:</strong> Develop and implement solutions to address root causes</p>
                        <p><strong className="text-blue-600">Control:</strong> Sustain gains by monitoring performance and standardizing improvements</p>
                      </div>
                      <p className="text-gray-700 mt-4">
                        By applying DMAIC to your business processes, you can systematically eliminate waste, reduce errors, and create consistent, high-quality outcomes that drive customer satisfaction and profitability.
                      </p>
                    </div>
                  </div>

                  {/* DMAIC Sections */}
                  <div className="max-w-5xl mx-auto space-y-8">
                    
                    {/* Define Section */}
                    <div className="border-2 border-blue-200 rounded-xl p-6 bg-gradient-to-r from-blue-50 to-white">
                      <div className="flex items-center mb-4">
                        <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-4">
                          D
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900">Define</h3>
                          <p className="text-sm text-gray-600">Clearly articulate the problem and goals</p>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-6 border border-gray-200">
                        <p className="text-gray-600 italic text-center">
                          Content for this section will be added soon. This is where you'll define your process improvement goals.
                        </p>
                      </div>
                    </div>

                    {/* Measure Section */}
                    <div className="border-2 border-green-200 rounded-xl p-6 bg-gradient-to-r from-green-50 to-white">
                      <div className="flex items-center mb-4">
                        <div className="w-12 h-12 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-4">
                          M
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900">Measure</h3>
                          <p className="text-sm text-gray-600">Collect data and establish baseline metrics</p>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-6 border border-gray-200">
                        <p className="text-gray-600 italic text-center">
                          Content for this section will be added soon. This is where you'll track current performance metrics.
                        </p>
                      </div>
                    </div>

                    {/* Analyze Section */}
                    <div className="border-2 border-yellow-200 rounded-xl p-6 bg-gradient-to-r from-yellow-50 to-white">
                      <div className="flex items-center mb-4">
                        <div className="w-12 h-12 bg-yellow-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-4">
                          A
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900">Analyze</h3>
                          <p className="text-sm text-gray-600">Identify root causes of inefficiencies</p>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-6 border border-gray-200">
                        <p className="text-gray-600 italic text-center">
                          Content for this section will be added soon. This is where you'll analyze data to find root causes.
                        </p>
                      </div>
                    </div>

                    {/* Improve Section */}
                    <div className="border-2 border-purple-200 rounded-xl p-6 bg-gradient-to-r from-purple-50 to-white">
                      <div className="flex items-center mb-4">
                        <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-4">
                          I
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900">Improve</h3>
                          <p className="text-sm text-gray-600">Develop and implement solutions</p>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-6 border border-gray-200">
                        <p className="text-gray-600 italic text-center">
                          Content for this section will be added soon. This is where you'll implement process improvements.
                        </p>
                      </div>
                    </div>

                    {/* Control Section */}
                    <div className="border-2 border-red-200 rounded-xl p-6 bg-gradient-to-r from-red-50 to-white">
                      <div className="flex items-center mb-4">
                        <div className="w-12 h-12 bg-red-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-4">
                          C
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900">Control</h3>
                          <p className="text-sm text-gray-600">Sustain gains and monitor performance</p>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-6 border border-gray-200">
                        <p className="text-gray-600 italic text-center">
                          Content for this section will be added soon. This is where you'll establish controls to maintain improvements.
                        </p>
                      </div>
                    </div>

                    {/* Complete Process Analysis Button */}
                    <div className="bg-gradient-to-r from-blue-50 to-green-50 border-2 border-blue-300 rounded-xl p-8 text-center">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">Ready to Review?</h3>
                      <p className="text-gray-700 mb-6">
                        Once you've reviewed the DMAIC framework and are ready to proceed, click below to unlock the Review tab.
                      </p>
                      <button
                        onClick={() => {
                          setProcessAnalysisComplete(true);
                          setActiveTab('auto-stack');
                        }}
                        className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition shadow-lg hover:shadow-xl"
                      >
                        {processAnalysisComplete ? '✓ Complete - View Review →' : '✓ Mark Process Analysis Complete'}
                      </button>
                    </div>
                  </div>

                  {/* Ad Banner */}
                  <div className="my-8 p-5 bg-white rounded-2xl shadow-sm">
                    <p className="text-center text-xs text-gray-500 mb-3">Advertisement</p>
                    <ins className="adsbygoogle"
                      style={{ display: 'block' }}
                      data-ad-client="ca-pub-6663686986943720"
                      data-ad-format="auto"
                      data-full-width-responsive="true"></ins>
                  </div>
                </div>
              )}


              {/* Plan Your Platform Tab */}
              {activeTab === 'plan' && (
                <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 animate-fadeIn">
                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                      <Rocket className="w-8 h-8 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-gray-900">Plan Your Platform</h2>
                      <p className="text-gray-600">Tell us about your business goals</p>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Basic Information */}
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-4">Your Information</h3>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <User className="inline w-4 h-4 mr-1" />
                            Full Name *
                          </label>
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => updateField('name', e.target.value)}
                            placeholder="John Doe"
                            required
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <Mail className="inline w-4 h-4 mr-1" />
                            Email Address *
                          </label>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => updateField('email', e.target.value)}
                            placeholder="john@example.com"
                            required
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <Phone className="inline w-4 h-4 mr-1" />
                            Phone Number *
                          </label>
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => updateField('phone', e.target.value)}
                            placeholder="(555) 123-4567"
                            required
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Business Plan Questions - Only show if Name, Email and Phone are filled */}
                    {!formData.name || !formData.email || !formData.phone ? (
                      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <ChevronRight className="w-6 h-6 text-blue-600" />
                          </div>
                          <div className="ml-3">
                            <h3 className="text-lg font-semibold text-blue-900 mb-2">
                              Please complete your contact information above
                            </h3>
                            <p className="text-blue-700">
                              We need your name, email address, and phone number before you can proceed with the business planning questionnaire.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Your Business</h3>
                        <div className="space-y-6">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              <Briefcase className="inline w-4 h-4 mr-1" />
                              Business or Project Name *
                            </label>
                            <input
                              type="text"
                              value={formData.businessName}
                              onChange={(e) => updateField('businessName', e.target.value)}
                              placeholder="e.g., Acme Online Courses"
                              required
                              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                            />
                          </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <FileText className="inline w-4 h-4 mr-1" />
                            Describe Your Business Idea *
                          </label>
                          <textarea
                            value={formData.businessDescription}
                            onChange={(e) => updateField('businessDescription', e.target.value)}
                            placeholder="What products or services will you offer? What problem does it solve?"
                            required
                            rows="4"
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <Target className="inline w-4 h-4 mr-1" />
                            What are Your Company's Top 3 Values?
                          </label>
                          <textarea
                            value={formData.companyValues}
                            onChange={(e) => updateField('companyValues', e.target.value)}
                            placeholder="e.g., People over profits, Transparency, Innovation, Sustainability, Customer-first approach..."
                            rows="3"
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                          />
                          <p className="text-sm text-gray-500 mt-1">Tell us what matters most to your business. This helps us understand your mission.</p>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <Users className="inline w-4 h-4 mr-1" />
                            Who is Your Target Market?
                          </label>
                          <input
                            type="text"
                            value={formData.targetMarket}
                            onChange={(e) => updateField('targetMarket', e.target.value)}
                            placeholder="e.g., Small business owners, fitness enthusiasts, etc."
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <DollarSign className="inline w-4 h-4 mr-1" />
                            How Will You Generate Revenue?
                          </label>
                          <select
                            value={formData.primaryRevenue}
                            onChange={(e) => updateField('primaryRevenue', e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                          >
                            <option value="">Select one...</option>
                            <option value="course-sales">Selling online courses</option>
                            <option value="memberships">Monthly membership/subscriptions</option>
                            <option value="services">Selling services (consulting, coaching, etc.)</option>
                            <option value="products">Physical products</option>
                            <option value="digital-products">Digital downloads (ebooks, templates, etc.)</option>
                            <option value="advertising">Advertising revenue</option>
                            <option value="mixed">Multiple revenue streams</option>
                            <option value="other">Other</option>
                          </select>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Current Stage
                            </label>
                            <select
                              value={formData.currentStage}
                              onChange={(e) => updateField('currentStage', e.target.value)}
                              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                            >
                              <option value="">Select one...</option>
                              <option value="idea">Just an idea</option>
                              <option value="planning">Planning phase</option>
                              <option value="building">Building content/products</option>
                              <option value="soft-launch">Soft launch/testing</option>
                              <option value="existing">Existing business looking to upgrade</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              <Calendar className="inline w-4 h-4 mr-1" />
                              When Do You Want to Launch?
                            </label>
                            <select
                              value={formData.launchTimeline}
                              onChange={(e) => updateField('launchTimeline', e.target.value)}
                              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                            >
                              <option value="">Select one...</option>
                              <option value="asap">As soon as possible</option>
                              <option value="1-month">Within 1 month</option>
                              <option value="3-months">1-3 months</option>
                              <option value="6-months">3-6 months</option>
                              <option value="exploring">Just exploring options</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <DollarSign className="inline w-4 h-4 mr-1" />
                            Monthly Budget for Platform + Infrastructure
                          </label>
                          <select
                            value={formData.monthlyBudget}
                            onChange={(e) => updateField('monthlyBudget', e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                          >
                            <option value="">Select one...</option>
                            <option value="under-100">Under $100/month</option>
                            <option value="100-200">$100-$200/month</option>
                            <option value="200-500">$200-$500/month</option>
                            <option value="500-1000">$500-$1,000/month</option>
                            <option value="over-1000">Over $1,000/month</option>
                            <option value="unsure">Not sure yet</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            What's Your Biggest Challenge Right Now?
                          </label>
                          <textarea
                            value={formData.mainChallenge}
                            onChange={(e) => updateField('mainChallenge', e.target.value)}
                            placeholder="e.g., Don't know how to set up payment processing, need help with content protection, etc."
                            rows="3"
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <Globe className="inline w-4 h-4 mr-1" />
                            Do You Already Have a Website or Domain?
                          </label>
                          <input
                            type="text"
                            value={formData.existingWebsite}
                            onChange={(e) => updateField('existingWebsite', e.target.value)}
                            placeholder="e.g., www.yourbusiness.com or 'Not yet'"
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Competitor Websites (if any)
                          </label>
                          <textarea
                            value={formData.competitorUrls}
                            onChange={(e) => updateField('competitorUrls', e.target.value)}
                            placeholder="List any competitors or similar platforms that inspire you (one per line)"
                            rows="3"
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Additional Notes or Questions
                          </label>
                          <textarea
                            value={formData.additionalNotes}
                            onChange={(e) => updateField('additionalNotes', e.target.value)}
                            placeholder="Anything else you'd like us to know?"
                            rows="4"
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                          />
                        </div>
                      </div>
                    </div>
                    )}

                    {/* Optional Website Details Section */}
                    {questionnaireComplete && (
                      <div className="mb-6">
                        <button
                          type="button"
                          onClick={() => setWebsiteDetailsExpanded(!websiteDetailsExpanded)}
                          className="w-full bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200  rounded-lg p-4  hover:from-purple-100 hover:to-blue-100 transition"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FileText className="w-6 h-6 text-purple-600" />
                              <div className="text-left">
                                <h3 className="text-lg font-bold text-gray-900">
                                  💡 Skip the phone call! Website Details (Optional)
                                </h3>
                                <p className="text-sm text-gray-600">
                                  Help us build your platform faster by pre-filling technical requirements
                                </p>
                              </div>
                            </div>
                            <ChevronDown 
                              className={`w-6 h-6 text-purple-600 transition-transform ${websiteDetailsExpanded ? 'transform rotate-180' : ''}`}
                            />
                          </div>
                        </button>

                        {websiteDetailsExpanded && (
                          <div className="mt-4 bg-white border-2 border-purple-100 rounded-lg p-6 space-y-6">
                            <p className="text-sm text-gray-600 italic bg-purple-50 p-3 rounded">
                              <strong>Note:</strong> This information helps us create your custom platform configuration file. All fields are optional but save time on consultation calls!
                            </p>

                            {/* Platform Requirements */}
                            <div className="border-b border-gray-200 pb-6">
                              <h4 className="text-md font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <Target className="w-5 h-5 text-purple-600" />
                                Platform Requirements
                              </h4>
                              <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-2">Business Type</label>
                                  <select value={formData.businessType} onChange={(e) => updateField('businessType', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200">
                                    <option value="">Select...</option>
                                    <option value="Content Creator">Content Creator</option>
                                    <option value="E-commerce">E-commerce</option>
                                    <option value="SaaS">SaaS</option>
                                    <option value="Consulting">Consulting</option>
                                    <option value="Agency">Agency</option>
                                    <option value="Real Estate">Real Estate</option>
                                    <option value="Other">Other</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-2">Platform Type</label>
                                  <select value={formData.platformType} onChange={(e) => updateField('platformType', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200">
                                    <option value="">Select...</option>
                                    <option value="vod">Video-on-Demand (VOD)</option>
                                    <option value="ecommerce">E-commerce</option>
                                    <option value="saas">SaaS Application</option>
                                    <option value="blog">Blog/Content Site</option>
                                    <option value="portfolio">Portfolio</option>
                                    <option value="booking">Booking/Scheduling</option>
                                    <option value="other">Other</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-2">Expected Monthly Traffic</label>
                                  <input type="text" value={formData.expectedTraffic} onChange={(e) => updateField('expectedTraffic', e.target.value)} placeholder="e.g., '100 visitors/month'" className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                                </div>
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-2">Expected Storage Needs</label>
                                  <input type="text" value={formData.expectedStorage} onChange={(e) => updateField('expectedStorage', e.target.value)} placeholder="e.g., '50GB'" className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                                </div>
                              </div>
                            </div>

                            {/* Branding */}
                            <div className="border-b border-gray-200 pb-6">
                              <h4 className="text-md font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <Target className="w-5 h-5 text-purple-600" />
                                Branding
                              </h4>
                              <div className="grid md:grid-cols-3 gap-4">
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-2">Primary Color (Hex)</label>
                                  <input type="text" value={formData.primaryColor} onChange={(e) => updateField('primaryColor', e.target.value)} placeholder="e.g., 2E5984" className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                                </div>
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-2">Secondary Color (Hex)</label>
                                  <input type="text" value={formData.secondaryColor} onChange={(e) => updateField('secondaryColor', e.target.value)} placeholder="e.g., 528AAE" className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                                </div>
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-2">Background Color (Hex)</label>
                                  <input type="text" value={formData.backgroundColor} onChange={(e) => updateField('backgroundColor', e.target.value)} placeholder="e.g., BCD2E8" className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                                </div>
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-2">Do you have a logo?</label>
                                  <select value={formData.hasLogo} onChange={(e) => updateField('hasLogo', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200">
                                    <option value="">Select...</option>
                                    <option value="yes">Yes</option>
                                    <option value="no">No</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-2">Font Style</label>
                                  <select value={formData.fontStyle} onChange={(e) => updateField('fontStyle', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200">
                                    <option value="">Select...</option>
                                    <option value="modern-sans">Modern Sans</option>
                                    <option value="classic-serif">Classic Serif</option>
                                    <option value="tech-mono">Tech Mono</option>
                                    <option value="elegant">Elegant</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-2">Brand Tone</label>
                                  <select value={formData.brandTone} onChange={(e) => updateField('brandTone', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200">
                                    <option value="">Select...</option>
                                    <option value="professional">Professional</option>
                                    <option value="warm">Warm & Friendly</option>
                                    <option value="bold">Bold & Edgy</option>
                                    <option value="minimal">Minimal</option>
                                  </select>
                                </div>
                              </div>
                            </div>

                            {/* Design & Styling */}
                            <div className="border-b border-gray-200 pb-6">
                              <h4 className="text-md font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <Target className="w-5 h-5 text-purple-600" />
                                Design & Styling
                              </h4>
                              <div className="grid md:grid-cols-3 gap-4">
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-2">Design Theme</label>
                                  <select value={formData.designTheme} onChange={(e) => updateField('designTheme', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200">
                                    <option value="">Select...</option>
                                    <option value="gradient">Gradient</option>
                                    <option value="flat">Flat</option>
                                    <option value="glassmorphism">Glassmorphism</option>
                                    <option value="neumorphism">Neumorphism</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-2">Layout Style</label>
                                  <select value={formData.layoutStyle} onChange={(e) => updateField('layoutStyle', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200">
                                    <option value="">Select...</option>
                                    <option value="single-page">Single Page</option>
                                    <option value="multi-page">Multi-Page</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-2">Button Style</label>
                                  <select value={formData.buttonStyle} onChange={(e) => updateField('buttonStyle', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200">
                                    <option value="">Select...</option>
                                    <option value="pill">Pill</option>
                                    <option value="rounded">Rounded</option>
                                    <option value="sharp">Sharp</option>
                                  </select>
                                </div>
                              </div>
                            </div>

                            {/* Business Model */}
                            <div className="border-b border-gray-200 pb-6">
                              <h4 className="text-md font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <DollarSign className="w-5 h-5 text-purple-600" />
                                Business Model
                              </h4>
                              <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-2">Revenue Model</label>
                                  <select value={formData.revenueModel} onChange={(e) => updateField('revenueModel', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200">
                                    <option value="">Select...</option>
                                    <option value="subscription">Subscription</option>
                                    <option value="one-time">One-Time Purchase</option>
                                    <option value="freemium">Freemium</option>
                                    <option value="free">Free</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-2">Pricing Tiers</label>
                                  <input type="text" value={formData.pricingTiers} onChange={(e) => updateField('pricingTiers', e.target.value)} placeholder="e.g., Basic $9, Pro $29, Enterprise $99" className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                                </div>
                              </div>
                            </div>

                            {/* Video Platform Specific */}
                            {formData.platformType === 'vod' && (
                              <div className="border-b border-gray-200 pb-6 bg-blue-50 p-4 rounded-lg">
                                <h4 className="text-md font-bold text-gray-900 mb-4">📹 Video Platform Settings</h4>
                                <div className="grid md:grid-cols-3 gap-4">
                                  <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Initial Video Count</label>
                                    <input type="number" value={formData.vodVideoCount} onChange={(e) => updateField('vodVideoCount', e.target.value)} placeholder="e.g., 10" className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Allow Downloads?</label>
                                    <select value={formData.vodDownloads} onChange={(e) => updateField('vodDownloads', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200">
                                      <option value="">Select...</option>
                                      <option value="yes">Yes</option>
                                      <option value="no">No</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Video Quality</label>
                                    <select value={formData.vodQuality} onChange={(e) => updateField('vodQuality', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200">
                                      <option value="">Select...</option>
                                      <option value="SD">SD (480p)</option>
                                      <option value="HD">HD (720p/1080p)</option>
                                      <option value="4K">4K</option>
                                    </select>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Additional Features */}
                            <div>
                              <h4 className="text-md font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <Zap className="w-5 h-5 text-purple-600" />
                                Additional Features
                              </h4>
                              <div className="space-y-4">
                                <div className="grid md:grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Chatbot Name</label>
                                    <input type="text" value={formData.chatbotName} onChange={(e) => updateField('chatbotName', e.target.value)} placeholder="e.g., AssistBot" className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Chatbot Personality</label>
                                    <select value={formData.chatbotPersonality} onChange={(e) => updateField('chatbotPersonality', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200">
                                      <option value="">Select...</option>
                                      <option value="friendly">Friendly</option>
                                      <option value="professional">Professional</option>
                                      <option value="casual">Casual</option>
                                      <option value="formal">Formal</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Google Analytics ID (optional)</label>
                                    <input type="text" value={formData.gaId} onChange={(e) => updateField('gaId', e.target.value)} placeholder="G-XXXXXXXXXX" className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                                  </div>
                                </div>
                                
                                <div className="border-t border-gray-200 pt-4 mt-4">
                                  <h5 className="text-sm font-bold text-gray-900 mb-3">Social Media Links (optional)</h5>
                                  <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                      <label className="block text-sm font-semibold text-gray-700 mb-2">Facebook</label>
                                      <input type="text" value={formData.facebook} onChange={(e) => updateField('facebook', e.target.value)} placeholder="Facebook profile/page URL" className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                                    </div>
                                    <div>
                                      <label className="block text-sm font-semibold text-gray-700 mb-2">Instagram</label>
                                      <input type="text" value={formData.instagram} onChange={(e) => updateField('instagram', e.target.value)} placeholder="@username" className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                                    </div>
                                    <div>
                                      <label className="block text-sm font-semibold text-gray-700 mb-2">Twitter/X</label>
                                      <input type="text" value={formData.twitter} onChange={(e) => updateField('twitter', e.target.value)} placeholder="@username" className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                                    </div>
                                    <div>
                                      <label className="block text-sm font-semibold text-gray-700 mb-2">LinkedIn</label>
                                      <input type="text" value={formData.linkedin} onChange={(e) => updateField('linkedin', e.target.value)} placeholder="LinkedIn profile URL" className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200" />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                          </div>
                        )}
                      </div>
                    )}

                    {/* Domain Search Section - Only show when questionnaire is complete */}
                    {questionnaireComplete && (
                      <div>
                        <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-lg p-6 mb-6">
                          <div className="flex items-start mb-4">
                            <div className="flex-shrink-0">
                              <Globe className="w-8 h-8 text-green-600" />
                            </div>
                            <div className="ml-3">
                              <h3 className="text-xl font-bold text-gray-900 mb-2">Find Your Perfect Domain</h3>
                              <p className="text-gray-700 mb-4">
                                Search for available domain names. Try different variations until you find one you love!
                              </p>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <div className="flex gap-3">
                              <div className="flex-1">
                                <input
                                  type="text"
                                  value={domainSearch}
                                  onChange={(e) => setDomainSearch(e.target.value)}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      checkDomainAvailability();
                                    }
                                  }}
                                  placeholder="example.com"
                                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={checkDomainAvailability}
                                disabled={!domainSearch.trim() || isCheckingDomain}
                                className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition ${
                                  domainSearch.trim() && !isCheckingDomain
                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                              >
                                {isCheckingDomain ? (
                                  <>
                                    <Loader className="w-5 h-5 animate-spin" />
                                    Checking...
                                  </>
                                ) : (
                                  <>
                                    <Search className="w-5 h-5" />
                                    Check
                                  </>
                                )}
                              </button>
                            </div>

                            {/* Domain Check Results */}
                            {domainCheckResult && (
                              <div className={`p-4 rounded-lg ${
                                domainCheckResult.error
                                  ? 'bg-red-50 border-2 border-red-200'
                                  : domainCheckResult.available
                                  ? 'bg-green-50 border-2 border-green-300'
                                  : 'bg-yellow-50 border-2 border-yellow-300'
                              }`}>
                                {domainCheckResult.error ? (
                                  <p className="text-red-800 font-semibold">{domainCheckResult.error}</p>
                                ) : domainCheckResult.available ? (
                                  <div>
                                    <p className="text-green-800 font-bold text-lg mb-2">
                                      ✓ {domainCheckResult.domain} is available!
                                    </p>
                                    <p className="text-green-700">
                                      We can work to confirm availability at our next consultation call.
                                    </p>
                                  </div>
                                ) : (
                                  <div>
                                    <p className="text-yellow-800 font-bold mb-2">
                                      ✗ {domainCheckResult.domain} is not available
                                    </p>
                                    <p className="text-yellow-700">
                                      Try a different name or variation above.
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}

                            {selectedDomain && (
                              <div className="bg-white border-2 border-green-400 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <Check className="w-6 h-6 text-green-600" />
                                    <div>
                                      <p className="text-sm text-gray-600">Selected Domain:</p>
                                      <p className="text-lg font-bold text-gray-900">{selectedDomain}</p>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedDomain('');
                                      setDomainCheckResult(null);
                                      setDomainSearch('');
                                    }}
                                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                                  >
                                    Search Again
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {!questionnaireComplete && formData.name && formData.email && formData.phone && (
                      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <ChevronRight className="w-6 h-6 text-blue-600" />
                          </div>
                          <div className="ml-3">
                            <h3 className="text-lg font-semibold text-blue-900 mb-2">
                              Almost there!
                            </h3>
                            <p className="text-blue-700">
                              Complete the questionnaire above to unlock domain search and submit your plan.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Domain Selection Bypass Option */}
                    {questionnaireComplete && !selectedDomain && (
                      <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={skipDomainSelection}
                            onChange={(e) => setSkipDomainSelection(e.target.checked)}
                            className="w-5 h-5 text-blue-600 rounded"
                          />
                          <span className="text-gray-700 font-medium">
                            I'll choose a domain later
                          </span>
                        </label>
                      </div>
                    )}

                    {/* Submit Button */}
                    <div className="bg-blue-50 rounded-lg p-6">
                      <button
                        type="submit"
                        disabled={!canSubmit || isSubmitting}
                        className={`w-full px-8 py-4 rounded-lg font-semibold text-lg transition ${
                          canSubmit && !isSubmitting
                            ? 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit My Plan'}
                      </button>
                      <p className="text-sm text-gray-600 text-center mt-3">
                        We'll review your information and get back to you within 24 hours
                      </p>
                    </div>
                  </form>

                  {/* Ad Banner */}
                  <div className="my-8 p-5 bg-white rounded-2xl shadow-sm">
                    <p className="text-center text-xs text-gray-500 mb-3">Advertisement</p>
                    <ins className="adsbygoogle"
                      style={{ display: 'block' }}
                      data-ad-client="ca-pub-6663686986943720"
                      data-ad-format="auto"
                      data-full-width-responsive="true"></ins>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PlanningLandingPage;
